/*
 * Icon — single source of truth for Hub iconography (DESIGN.md §9).
 *
 * The direction is custom 24x24 silver-line SVGs, designed in Canva Pro and
 * dropped into src/assets/icons/ as `icon-{name}.svg`. But those are being
 * drawn at Bobbie's pace, so this component ships the *infrastructure* now:
 *
 *   <Icon name="bell" />
 *
 * resolves in this order:
 *   1. If src/assets/icons/icon-bell.svg exists, render it inline (so it
 *      recolors via `currentColor` and stays crisp on retina).
 *   2. Otherwise fall back to the Unicode emoji mapped below.
 *
 * That way every screen can adopt <Icon> today, and each finished SVG just
 * drops into place — no waterfall, no per-screen edits. Change one file,
 * every screen updates.
 *
 * Color is driven by the surrounding `currentColor` (DESIGN.md §9 color
 * rules), so wrap an Icon in an element with the color you want.
 */

// Eagerly slurp every icon-*.svg as a raw string. Empty today; populated as
// SVGs land. Vite returns {} when nothing matches, so this is safe.
const svgModules = import.meta.glob('../assets/icons/icon-*.svg', {
  query: '?raw',
  import: 'default',
  eager: true,
}) as Record<string, string>;

// Index by intent name parsed out of "../assets/icons/icon-{name}.svg".
const svgByName: Record<string, string> = {};
for (const [path, raw] of Object.entries(svgModules)) {
  const match = path.match(/icon-([a-z0-9-]+)\.svg$/i);
  if (match) svgByName[match[1]] = raw;
}

/**
 * Unicode-emoji fallback per intent. Mirrors the emoji currently used across
 * the Hub (DESIGN.md §9 Phase 1 + Phase 2 tables) so swapping <Icon> in for a
 * raw emoji is visually a no-op until the SVG arrives.
 */
const EMOJI: Record<string, string> = {
  // Phase 1
  bell: '🔔',
  clipboard: '📋',
  sos: '🆘',
  'graduation-cap': '🎓',
  megaphone: '📣',
  newspaper: '📰',
  star: '⭐',
  calendar: '📅',
  warning: '⚠️',
  'chat-bubble': '💬',
  'thought-bubble': '💭',
  pin: '📌',
  camera: '📷',
  link: '🔗',
  video: '🎥',
  // Phase 2
  'wave-hello': '👋',
  // The menu's Events row. SVG-backed since 2026-10-20 (Bobbie, on the menu:
  // "Change emoji to match the rest of the set uo"): a line party popper in
  // the same stroke as Calendar and To-dos, so the fallback stays empty like
  // trophy's and growth's.
  party: '',
  lock: '🔒',
  check: '✓',
  cross: '✗',
  microphone: '🎤',
  mic: '🎤',
  dancer: '💃',
  'hip-hop': '💃',
  'room-door': '🚪',
  clock: '⏰',
  search: '🔍',
  // SVG-backed. Keep the fallback empty so an unavailable asset never turns
  // into a platform-dependent emoji on older phones.
  trophy: '',
  // Nav-tile intents (added for the home-screen grid).
  enroll: '📝',
  people: '👥',
  // Theme toggle.
  sun: '☀️',
  gear: '⚙️',
  moon: '🌙',
  // Phase 3 — empty states, gates, inline action glyphs (custom SVGs added).
  music: '🎵',
  image: '🖼️',
  chart: '📊',
  building: '🏛️',
  inbox: '📬',
  download: '📥',
  grid: '🗓️',
  'no-entry': '🚫',
  // Tasks detail's "Add to my device reminders" - the Web Share
  // API hands the task off to whatever reminder app the share sheet offers.
  share: '📤',
  // Student evaluation (the route is still /growth), the twice-a-year
  // progress reports. SVG-backed since 2026-09-28 (Bobbie: "change the
  // evaluation emoji", then "yes lets do a clipboard"): a clipboard with
  // written lines, distinct from To-dos' checked clipboard, in the same stroke
  // style as the other home tiles, so the fallback stays empty like trophy's.
  growth: '',
};

export type IconName = keyof typeof EMOJI;

interface IconProps {
  name: IconName | (string & {});
  /** Rendered square size in px. Defaults to 20 (nav-row scale). */
  size?: number;
  /** Accessible label. Omit for purely decorative icons (then aria-hidden). */
  title?: string;
  style?: React.CSSProperties;
}

export function Icon({ name, size = 20, title, style }: IconProps) {
  const svg = svgByName[name];
  const a11y = title
    ? { role: 'img' as const, 'aria-label': title }
    : { 'aria-hidden': true };

  if (svg) {
    return (
      <span
        className="ldg-icon"
        style={{ width: size, height: size, ...style }}
        // The SVG should author its strokes/fills as currentColor so it
        // inherits the surrounding text color (DESIGN.md §9).
        dangerouslySetInnerHTML={{ __html: svg }}
        {...a11y}
      />
    );
  }

  // Emoji fallback. Sized a touch smaller than the box so it sits centered.
  const emoji = EMOJI[name] ?? '•';
  return (
    <span
      className="ldg-icon"
      style={{
        width: size,
        height: size,
        fontSize: Math.round(size * 0.95),
        ...style,
      }}
      {...a11y}
    >
      {emoji}
    </span>
  );
}

export default Icon;
